import { useEffect, useState } from 'react';
import { OpmCalculationResultsDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { ProjectDto } from '@app/shared/models/contracts/project-dto';
import * as OpmCalculationActions from '@app/core/store/opm-calculation-slice';
import * as ExportActions from '@app/core/store/export-slice';
import * as UiActions from '@app/core/store/ui-values-slice';
import * as NotificationActions from '@app/core/store/notification-slice';
import * as ProjectActions from '@app/core/store/project-slice';
import env from '@environment';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { OpmCalculationType } from '../store/opm-calculation-slice';
import { SignalrMessageTypes } from './SignalrMessageTypes';
import store from '../store/store';
import { SeverityType } from '@app/shared/mui-components/alert/AlertTypes';
import { StatusCodes } from '@app/shared/enums/status-codes';
import { LOADER_HIDE_TIMEOUT, LOADER_TIMEOUT } from '@app/shared/constants/duration';
import { OperationProgressUpdateDto } from '@app/shared/models/contracts/operation-progress-update-dto';
import { WithValidationResult } from '@app/shared/interfaces/with-validation-result';
import {
  ClientScheduleExportType,
  ValidationJobType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { t } from 'i18next';
import { translationKeys } from '@locale/setupI18n';
import { SystemSettingsDto } from '@app/shared/models/contracts/application-configuration-dto';
import { RootAppRoutes } from '@app/shared/enums/root-app-routes';

const RECONNECT_PERIOD = 2000;
const TAB_INACTIVE_TIMEOUT = 120000; // 2 minutes

const connection = new HubConnectionBuilder()
  .withUrl(`${env.apiUrl}/hubs/signalr`)
  .configureLogging('Information')
  .build();

// Tab visibility tracking variables
let disconnectTimer: NodeJS.Timeout | null = null;
let isConnectionPausedDueToInactivity = false;

// Track active operations to prevent disconnection
type OperationName =
  | 'OpmCalculation'
  | 'OpmExport'
  | 'SaveProject'
  | 'ClientScheduleExport'
  | 'OpmCalculationOnSave';
const activeOperations: Record<string, boolean> = {};

// Status update subscribers for React components
type StatusUpdateCallback = () => void;
const statusUpdateSubscribers: Set<StatusUpdateCallback> = new Set();

const notifyStatusUpdate = () => {
  statusUpdateSubscribers.forEach((callback) => callback());
};

const setOperationActive = (operationName: OperationName, operationId?: string) => {
  const key = operationId ? `${operationName}_${operationId}` : operationName;
  activeOperations[key] = true;
  notifyStatusUpdate();
};

const setOperationComplete = (operationName: OperationName, operationId?: string) => {
  const key = operationId ? `${operationName}_${operationId}` : operationName;
  delete activeOperations[key];
  notifyStatusUpdate();

  // If no more active operations and tab is inactive, restart the disconnect timer
  const hasActiveOps = Object.keys(activeOperations).some((k) => activeOperations[k]);
  if (!hasActiveOps && document.visibilityState === 'hidden' && !disconnectTimer) {
    disconnectTimer = setTimeout(() => {
      disconnectTimer = null;
      stopConnection();
    }, TAB_INACTIVE_TIMEOUT);
    notifyStatusUpdate();
  }
};

// React hook to access SignalR status
export const useSignalRStatus = () => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const callback = () => forceUpdate({});
    statusUpdateSubscribers.add(callback);
    return () => {
      statusUpdateSubscribers.delete(callback);
    };
  }, []);

  return {
    connectionState: connection.state,
    isTabVisible: document.visibilityState === 'visible',
    hasDisconnectTimer: disconnectTimer !== null,
    activeOperations: { ...activeOperations },
    isConnectionPaused: isConnectionPausedDueToInactivity,
  };
};

const isOnNoAccessPage = (): boolean => {
  return location.pathname.indexOf(RootAppRoutes.Forbidden) !== -1;
};

const start = async () => {
  try {
    await connection.start();
    isConnectionPausedDueToInactivity = false;
    notifyStatusUpdate();
  } catch (err) {
    if (isOnNoAccessPage()) {
      return;
    }

    setTimeout(start, RECONNECT_PERIOD);
  }
};

const stopConnection = async () => {
  // Don't disconnect if there are active operations in progress
  const hasActiveOps = Object.keys(activeOperations).some((k) => activeOperations[k]);
  if (hasActiveOps) {
    return;
  }
  if (connection.state === HubConnectionState.Connected) {
    isConnectionPausedDueToInactivity = true;
    await connection.stop();
    notifyStatusUpdate();
  }
};

connection.onclose(async () => {
  notifyStatusUpdate();
  if (!isConnectionPausedDueToInactivity) {
    await start();
  }
});

connection.on(
  SignalrMessageTypes.OpmCalculationComplete,
  async (results: OpmCalculationResultsDto, type: OpmCalculationType) => {
    setOperationComplete('OpmCalculation');
    if (type === OpmCalculationType.RealWorld) {
      await new Promise((resolve) => setTimeout(resolve, LOADER_HIDE_TIMEOUT));
      store.dispatch(OpmCalculationActions.processOpmRealWorldResults(results));
    } else {
      store.dispatch(OpmCalculationActions.processOpmRiskFreeResults(results));
    }
  }
);

connection.on(SignalrMessageTypes.OpmExportComplete, (jobId: number) => {
  setOperationComplete('OpmExport');
  store.dispatch(ExportActions.downloadExport(jobId));
});

connection.on(SignalrMessageTypes.ClientScheduleExportComplete, (jobId: number) => {
  setOperationComplete('ClientScheduleExport');
  store.dispatch(ExportActions.downloadExport(jobId));
});

connection.on(SignalrMessageTypes.SaveComplete, () => {
  setOperationComplete('SaveProject');
  store.dispatch(UiActions.setDisplayOperationProgress(false));
  store.dispatch(ProjectActions.saveComplete());
  store.dispatch(UiActions.setIsSaving(false));
});

connection.on(SignalrMessageTypes.OpmCalculationRunOnSave, () => {
  store.dispatch(UiActions.setDisplayOperationProgress(true));
  store.dispatch(UiActions.setOpmCalcRunningStatus(true));
});

connection.on(SignalrMessageTypes.SystemSettingsUpdate, (settings: SystemSettingsDto) => {
  store.dispatch(UiActions.setSystemSettings(settings));
});

connection.on(SignalrMessageTypes.OperationProgressUpdate, (update: OperationProgressUpdateDto) => {
  store.dispatch(UiActions.setOperationProgress(update));
});

connection.on(SignalrMessageTypes.OperationError, (errorCode: number, errorMessage: string) => {
  Object.keys(activeOperations).forEach((key) => {
    const [operationName] = key.split('_');
    setOperationComplete(operationName as OperationName);
  });
  store.dispatch(
    NotificationActions.showNotificationSnackbar({
      severity: SeverityType.error,
      message: errorMessage,
      autoHide: true,
      errorCode: StatusCodes.BadRequest,
      errorInstanceId: undefined,
    })
  );
  store.dispatch(UiActions.setDisplayOperationProgress(false));
  store.dispatch(UiActions.setIsSaving(false));
});

connection.on(
  SignalrMessageTypes.ValidationError,
  (validationResult: WithValidationResult<null>) => {
    if (!validationResult.validation.isValid && validationResult.validation.errors.length > 0) {
      const jobType = validationResult.validationJobType;
      const errors = validationResult.validation.errors;
      const message = errors[0].errorMessage;
      const translationKey = getValidationMessageKey(jobType);

      // turn off escaping for the i18next interpolation so it doesn't
      // mangle quote marks etc. This is OK to do as we trust the source
      // of the error messages (fluentvalidation in the backend API)
      store.dispatch(
        NotificationActions.showNotificationSnackbar({
          severity: SeverityType.error,
          message: t(translationKey, { message: message, interpolation: { escapeValue: false } }),
          errorInstanceId: undefined,
          autoHide: true,
        })
      );
    }

    store.dispatch(UiActions.setDisplayOperationProgress(false));
    store.dispatch(UiActions.setIsSaving(false));
  }
);

start();

// Handle tab visibility change
document.addEventListener('visibilitychange', () => {
  notifyStatusUpdate();

  if (document.visibilityState === 'visible') {
    // Tab became active - cancel any pending disconnect and reconnect if needed
    if (disconnectTimer) {
      clearTimeout(disconnectTimer);
      disconnectTimer = null;
      notifyStatusUpdate();
    }

    if (connection?.state !== HubConnectionState.Connected) {
      start();
    }
  } else {
    // Tab became inactive - start timer to disconnect after 2 minutes
    if (disconnectTimer) {
      clearTimeout(disconnectTimer);
    }

    // Only set disconnect timer if there are no active operations
    const hasActiveOps = Object.keys(activeOperations).some((k) => activeOperations[k]);
    if (!hasActiveOps) {
      disconnectTimer = setTimeout(() => {
        stopConnection();
      }, TAB_INACTIVE_TIMEOUT);
      notifyStatusUpdate();
    } else {
      notifyStatusUpdate();
    }
  }
});

const getValidationMessageKey = (jobType: ValidationJobType): translationKeys => {
  switch (jobType) {
    case ValidationJobType.OpmCalculation:
      return '_OpmCalculationFailedWithMessage';
    case ValidationJobType.OpmExport:
      return '_OpmExportFailedWithMessage';
    case ValidationJobType.Save:
      return '_ProjectSaveFailedWithMessage';
    case ValidationJobType.ClientScheduleExport:
      return '_ClientScheduleExportFailedWithMessage';
    default:
      throw new Error('Unknown Validation Job Type');
  }
};

// Cleanup function (if needed for testing or unmounting)
export const cleanupSignalRConnection = () => {
  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
  }
};

export const runOpmCalculation = async (
  projectId: number,
  userId: number,
  project: ProjectDto,
  type: OpmCalculationType
) => {
  setOperationActive('OpmCalculation'); // Operation started
  store.dispatch(UiActions.setDisplayOperationProgress(true));
  store.dispatch(UiActions.setOpmCalcRunningStatus(true));
  await new Promise((resolve) => setTimeout(resolve, LOADER_TIMEOUT));
  connection.send(SignalrMessageTypes.RunOpmCalculation, projectId, userId, project, type);
};

const OPM_CALCULATION_RESULT_TIMEOUT = 600000; // 10 minutes

// Runs an OPM calculation and resolves with the raw results, bypassing the redux store,
// for callers (e.g. AI package export) that need the response directly.
export const runOpmCalculationAndAwaitResult = (
  projectId: number,
  userId: number,
  project: ProjectDto,
  type: OpmCalculationType
): Promise<OpmCalculationResultsDto> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      connection.off(SignalrMessageTypes.OpmCalculationComplete, handler);
      reject(new Error(`Timed out waiting for OPM calculation result of type '${type}'.`));
    }, OPM_CALCULATION_RESULT_TIMEOUT);

    const handler = (results: OpmCalculationResultsDto, resultType: OpmCalculationType) => {
      if (resultType !== type) {
        return;
      }
      clearTimeout(timeoutId);
      connection.off(SignalrMessageTypes.OpmCalculationComplete, handler);
      resolve(results);
    };

    connection.on(SignalrMessageTypes.OpmCalculationComplete, handler);
    runOpmCalculation(projectId, userId, project, type).catch((err) => {
      clearTimeout(timeoutId);
      connection.off(SignalrMessageTypes.OpmCalculationComplete, handler);
      reject(err);
    });
  });
};

export const runOpmExport = async (
  projectId: number,
  userId: number,
  project: ProjectDto,
  type: OpmCalculationType
) => {
  setOperationActive('OpmExport'); // Operation started
  store.dispatch(UiActions.setDisplayOperationProgress(true));
  store.dispatch(UiActions.setExportRunningStatus(true));
  await new Promise((resolve) => setTimeout(resolve, LOADER_TIMEOUT));
  connection.send(SignalrMessageTypes.RunOpmExport, projectId, userId, project, type);
};

export const saveProject = async (projectId: number, userId: number, project: ProjectDto) => {
  setOperationActive('SaveProject'); // Operation started
  connection.send(SignalrMessageTypes.SaveProject, projectId, userId, project);
};

export const runClientScheduleExport = async (
  projectId: number,
  userId: number,
  project: ProjectDto,
  exportType: ClientScheduleExportType,
  password: Nullable<string>
) => {
  setOperationActive('ClientScheduleExport'); // Operation started
  store.dispatch(UiActions.setDisplayOperationProgress(true));
  store.dispatch(UiActions.setExportRunningStatus(true));
  await new Promise((resolve) => setTimeout(resolve, LOADER_TIMEOUT));
  connection.send(
    SignalrMessageTypes.RunClientScheduleExport,
    projectId,
    userId,
    project,
    exportType,
    password
  );
};
